import { HttpErrorResponse, HttpInterceptorFn } from '@angular/common/http';
import { inject } from '@angular/core';
import { Observable, catchError, switchMap, throwError } from 'rxjs';
import { TokenService } from '@core/services/token.service';
import { AuthApiService } from '@core/services/api/auth-api.service';

const AUTH_ENDPOINTS = ['/auth/login', '/auth/refresh'];

function isAuthEndpoint(url: string): boolean {
  return AUTH_ENDPOINTS.some(endpoint => url.includes(endpoint));
}

/**
 * Attaches the stored access token to every outgoing request (except the
 * login/refresh calls themselves, which must not carry a stale token). On a
 * 401 response it attempts a single refresh-token retry; if that also fails,
 * tokens are cleared and the 401 is left to propagate (the auth guard will
 * redirect on the next navigation).
 */
export const authInterceptor: HttpInterceptorFn = (req, next) => {
  const tokenService = inject(TokenService);
  const authApiService = inject(AuthApiService);

  const skipAuth = isAuthEndpoint(req.url);
  const accessToken = tokenService.getAccessToken();

  const authorizedReq = !skipAuth && accessToken
    ? req.clone({ setHeaders: { Authorization: `Bearer ${accessToken}` } })
    : req;

  return next(authorizedReq).pipe(
    catchError((error: unknown) => {
      if (!(error instanceof HttpErrorResponse) || error.status !== 401 || skipAuth) {
        return throwError(() => error);
      }

      const refreshToken = tokenService.getRefreshToken();
      if (!refreshToken) {
        tokenService.clear();
        return throwError(() => error);
      }

      return authApiService.refresh({ refreshToken }).pipe(
        switchMap(tokens => {
          tokenService.setTokens(tokens.accessToken, tokens.refreshToken);
          const retriedReq = req.clone({
            setHeaders: { Authorization: `Bearer ${tokens.accessToken}` }
          });
          return next(retriedReq);
        }),
        catchError((refreshError: unknown): Observable<never> => {
          tokenService.clear();
          return throwError(() => refreshError);
        })
      );
    })
  );
};
